/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect } from "react";

import {
  ANALYTICS_EVENTS,
  ANALYTICS_GA_CATEGORIES,
} from "../../shared/constants/analyticsKeys";
import { usePlayerController, usePlayerFeatures } from "../BasePlayer";
import { useCustomPlayerContext } from "../CustomPlayerContext";

import { usePlayerCurrentMedia } from "./PlayerCurrentMediaContext";

export const useTrackAnalytics = () => {
  const { player } = usePlayerController();
  const { onAnalyticsEvent } = useCustomPlayerContext();
  const { trackAnalytics: trackAnalyticsEnabled } = usePlayerFeatures();

  const {
    media: { Title: mediaTitle = "unknown" },
  } = usePlayerCurrentMedia();

  const sendEvent = useCallback(
    (eventName: string) => {
      if (!trackAnalyticsEnabled) {
        return;
      }
      onAnalyticsEvent?.({
        name: eventName,
        gaCategory: ANALYTICS_GA_CATEGORIES.VIDEO_PLAYER,
        gaLabel: mediaTitle,
        params: { media_title: mediaTitle },
      });
    },
    [mediaTitle, trackAnalyticsEnabled, onAnalyticsEvent],
  );

  useEffect(() => {
    const unsubscribe = player.onEvent("ready", () =>
      sendEvent(ANALYTICS_EVENTS.VIDEO_PLAYED),
    );

    return () => {
      unsubscribe();
    };
  }, [player, sendEvent]);

  useEffect(() => {
    const unsubscribe = player.onEvent("dispose", () =>
      sendEvent(ANALYTICS_EVENTS.VIDEO_DROPPED),
    );

    return () => {
      unsubscribe();
    };
  }, [player, sendEvent]);
};
